<script setup>
import {storeToRefs} from "pinia";
import ProjectMenu from "@/components/projects/ProjectMenu.vue";
import SettingsMenu from "@/components/settings/SettingsMenu.vue";

const uiStore = useUiStore()
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const projectStore = useProjectStore()

const { 'currentItem': currentProject } = storeToRefs(projectStore)
const { sideBarState } = storeToRefs(uiStore)
const group = ref(null)
const selectedItems = ref([])
const onlyEnableTermsAndConditions = computed(() => !authStore.$state.user?.hasAcceptedTermsOfService)

const currentRoute = computed({
  get() {
    return router.currentRoute?.value?.name || '';
  },
})

// Hide menu if user logs off and goes to logon screen
watch(() => authStore.$state.user, (newValue) => {
  if(!newValue){
    sideBarState.value = false
  } else {
    sideBarState.value = true
  }
})

watch(group, async () => {
  sideBarState.value = false
})

watch(currentRoute, (newValue) => {
  if (newValue !== 'project-index' && newValue.startsWith('project-')) {
    selectedItems.value = []
  }
})

onMounted(() => {
  if(!authStore.$state.user){
    sideBarState.value = false
  }
})
</script>

<template>
  <v-navigation-drawer
    v-model="sideBarState"
    v-if="sideBarState === true && !route.name?.startsWith('auth-')"
    location="left"
    :width="300"
  >
    <div class="d-flex flex-column justify-space-between fill-height">
      <v-list
        v-model:selected="selectedItems"
        density="compact"
        class="pa-0 d-flex flex-column justify-lg-space-between fill-height"
        :nav="true"
        :mandatory="true"
      >
        <div class="overflow-y-auto">
          <template v-if="authStore.isGlobalAdmin">
            <v-list-item
              :disabled="onlyEnableTermsAndConditions"
              :active="route.name === 'user-index'"
              class="pt-3 pb-3 mb-0" @click="router.push({ name: 'user-index' })"
              prepend-icon="mdi-account"
              value="user-management"
              color="primary"
            >
              Users
            </v-list-item>
            <v-divider></v-divider>
          </template>
          <template v-if="authStore.hasServicePermission('projects', ['find_all', 'find_assigned'])">
            <v-list-item
              :disabled="onlyEnableTermsAndConditions"
              :active="route.name === 'project-index'"
              class="pt-3 pb-3 mb-0" @click="router.push({ name: 'project-index' })"
              prepend-icon="mdi-briefcase"
              value="projects"
              color="primary">
              Projects
            </v-list-item>
            <v-divider></v-divider>
            <div v-if="currentRoute.startsWith('project-') && currentRoute !== 'project-index' && currentProject">
              <project-menu></project-menu>
            </div>
          </template>
          <template v-if="authStore.hasServicePermission('polygons', ['find_all', 'find_assigned'])">
            <v-list-item
              :disabled="onlyEnableTermsAndConditions"
              :active="route.name === 'polygon-index'"
              class="pt-3 pb-3 mb-0" @click="router.push({ name: 'polygon-index' })"
              prepend-icon="mdi-vector-polygon"
              value="polygons"
              color="primary">
              All Polygons
            </v-list-item>
            <v-divider></v-divider>
          </template>
          <template v-if="authStore.hasServicePermission('data-exports', ['find_all', 'find_assigned'])">
            <v-list-item
              :disabled="onlyEnableTermsAndConditions"
              :active="route.name === 'exported-data-index'"
              class="pt-3 pb-3 mb-0"
              @click="router.push({ name: 'exported-data-index' })"
              prepend-icon="mdi-file-export"
              value="data-export"
              color="primary"
              >
              Data Exports
            </v-list-item>
            <v-divider></v-divider>
          </template>
          <template v-if="authStore.isGlobalAdmin">
            <v-list-item
                :disabled="onlyEnableTermsAndConditions"
                :active="route.name === 'settings-index'"
                class="pt-3 pb-3 mb-0" @click="router.push({ name: 'settings-index' })"
                prepend-icon="mdi-cog"
                value="settings"
                color="primary"
            >
              Settings
            </v-list-item>
            <v-divider></v-divider>
            <div v-if="currentRoute.startsWith('settings-') && currentRoute !== 'settings-index'">
              <settings-menu></settings-menu>
            </div>
          </template>
        </div>
        <div>
          <v-list-item
            :active="route.name === 'terms-and-conditions'"
            @click="router.push({ name: 'terms-and-conditions' })"
            prepend-icon="mdi-file-document"
            value="terms-and-conditions"
            color="primary"
          >
            Terms and Conditions
          </v-list-item>
        </div>
      </v-list>
    </div>
  </v-navigation-drawer>
</template>

<style>
.v-navigation-drawer .v-icon {
  color: #009EAB !important;
  opacity: 1 !important;
}
</style>